{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "behavior",
  "x-cli-editable": true,
  "title": "Behavior",
  "description": "Agent conversation behavior: response length and personality settings.",
  "type": "object",
  "properties": {
    "response_length": {
      "ui:sectionTitle": "Responses",
      "ui:sectionDescription": "How long and how the agent writes.",
      "ui:sectionIcon": "MessageSquare",
      "type": "string",
      "enum": [
        "normal",
        "detailed",
        "long"
      ],
      "ui:widget": "enum-slider",
      "ui:tooltip": "Controls how long the agent's responses are.",
      "ui:description": "Normal mode produces concise, chat-style answers and uses fewer tokens — we recommend it for most use cases. Detailed mode gives fuller explanations. Long mode generates thorough responses but is significantly less efficient."
    },
    "response_tone": {
      "type": "string",
      "x-tier": "advanced",
      "ui:widget": "textarea-with-presets",
      "ui:title": "Tone guidelines",
      "ui:tooltip": "Instructions that shape the agent's writing style and personality.",
      "ui:description": "Use a preset as a starting point, then edit freely to match your brand voice. Language Architecture rules (prohibited expressions) are always enforced separately.",
      "ui:presets": {
        "Professional": "- **Natural, human, conversational** tone that remains professional\n- **Clear, factual and confident** but never robotic or scripted\n- **Sound like a knowledgeable colleague** explaining how something works\n- **Do not oversell or exaggerate** but convince with accurate facts, benefits and examples\n- **Be neutral, factual and nuanced** - neutrality is more compelling and builds trust\n- **Avoid over-enthusiastic language** - don't use \"Amazing\", \"Fantastic\", \"Excellent\", \"Absolutely\", \"Love\"\n- **Don't use jargon or specialist language** - rephrase it so it's clear and easy for anyone to understand\n- **You're in a conversation**, not just answering isolated questions",
        "Sales-oriented": "- **Warm, energetic and enthusiastic** - show genuine excitement about what the product can do for the visitor\n- **Confident and persuasive** - highlight benefits and outcomes, not just features\n- **Clear and concrete** - back up every claim with specific facts, numbers, or examples\n- **Sound like a passionate colleague** who truly believes in the product and wants to help\n- **Don't use jargon or specialist language** - rephrase it so it's clear and easy for anyone to understand\n- **You're in a conversation**, not delivering a pitch - stay responsive to what the visitor actually asked"
      }
    },
    "use_visitor_context": {
      "ui:sectionTitle": "Personalization",
      "ui:sectionDescription": "How the agent adapts to each visitor.",
      "ui:sectionIcon": "Users",
      "type": "boolean",
      "ui:tooltip": "Personalize the conversation based on the visitor's detected company and industry. When disabled, the agent treats every visitor the same — generated answers will not reference the visitor's company or sector.",
      "ui:description": "Disable this if you don't want the agent to tailor responses based on IP-based company detection (e.g., for demo environments or when company detection is unreliable)."
    },
    "playbook": {
      "type": "string",
      "enum": [
        "information",
        "conversion"
      ],
      "ui:title": "Playbook",
      "ui:enumLabels": {
        "information": "Information-first",
        "conversion": "Conversion-first"
      },
      "ui:tooltip": "What the agent prioritizes: informative answers or moving visitors toward a demo.",
      "ui:description": "Information-first: prioritize clear, informative responses over conversion; answer thoroughly and let visitors self-serve, offering a demo only when they are clearly ready or ask for one. Conversion-first: prioritize conversion; act as an SDR that builds interest, keeps answers non-terminal, and guides qualified visitors toward a demo."
    },
    "demo_timing": {
      "type": "string",
      "x-access": "staff",
      "enum": [
        "interest_score",
        "readiness_judge"
      ],
      "ui:title": "Demo timing",
      "ui:show-when": { "playbook": "information" },
      "ui:tooltip": "How the agent decides when to offer a demo (overridden by the Conversion-first playbook).",
      "ui:description": "Interest score: offer a demo once accumulated interest points cross the threshold. Readiness judge: offer a demo only when an AI SDR sees a genuine buying signal (asked pricing, showed pull), so conversations continue longer instead of pitching too early. Note: the Conversion-first playbook always uses the Readiness judge — this setting only applies to the Information-first playbook."
    }
  },
  "default": {
    "response_length": "normal",
    "use_visitor_context": true,
    "playbook": "information",
    "demo_timing": "interest_score",
    "response_tone": "- **Natural, human, conversational** tone that remains professional\n- **Clear, factual and confident** but never robotic or scripted\n- **Sound like a knowledgeable colleague** explaining how something works\n- **Do not oversell or exaggerate** but convince with accurate facts, benefits and examples\n- **Be neutral, factual and nuanced** - neutrality is more compelling and builds trust\n- **Avoid over-enthusiastic language** - don't use \"Amazing\", \"Fantastic\", \"Excellent\", \"Absolutely\", \"Love\"\n- **Don't use jargon or specialist language** - rephrase it so it's clear and easy for anyone to understand\n- **You're in a conversation**, not just answering isolated questions"
  }
}
